import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Check } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useBilling, usePlans } from '../hooks/useBilling';
import { billingAction, priceLabel, trialCta, trialLength } from '../lib/billing';
import { RegistrationProgress } from '../components/RegistrationProgress';
import { AuthShell } from '../components/auth/AuthField';
import { NotebookCover } from '../components/auth/NotebookCover';
import { notebookTitle } from '../lib/notebookTitle';

// Same four lines as mobile's paywall (mobile/src/features/billing/paywall-sections.tsx).
const FEATURES = [
  'Unlimited scans, every page',
  'Transcription with titles, tags & summaries',
  'Ask anything across all your notebooks',
  'Collections, search & sharing',
] as const;

const formatDate = (iso: string | null): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

/** Two-stop "today → when the trial ends" timeline so the terms read at a glance. */
const TrialTimeline: React.FC<{ trial: string; price: string }> = ({ trial, price }) => (
  <div className="flex gap-4">
    <div className="flex flex-col items-center py-1">
      <span className="h-3 w-3 rounded-full border-2 border-solid border-accent bg-accent" />
      <span className="my-1 w-0.5 flex-1 bg-[rgba(91,63,208,0.25)]" />
      <span className="h-3 w-3 rounded-full border-2 border-solid border-[rgba(91,63,208,0.4)] bg-surface" />
    </div>
    <div className="flex flex-1 flex-col gap-5">
      <div>
        <p className="m-0 font-body text-[0.8125rem] font-semibold text-on-surface">Today</p>
        <p className="m-0 mt-0.5 font-body text-[0.8125rem] text-on-surface-variant">Full access, free for {trial}</p>
      </div>
      <div>
        <p className="m-0 font-body text-[0.8125rem] font-semibold text-on-surface">In {trial}</p>
        <p className="m-0 mt-0.5 font-body text-[0.8125rem] text-on-surface-variant">{price} · cancel anytime before then</p>
      </div>
    </div>
  </div>
);

/**
 * The paywall — the web twin of mobile's (mobile/app/paywall.tsx): your notebook
 * cover, what you get, and one ink button. Subscribers see their status instead.
 */
export const Subscription: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { plan, isLoading: plansLoading } = usePlans();
  const { busy, subscribe, openPortal } = useBilling();

  if (!user) return null;

  const action = billingAction(user);
  const periodEnd = formatDate(user.current_period_end);
  const price = plan ? priceLabel(plan) : null;
  const trial = plan ? trialLength(plan.trial_days) : null;

  const title = {
    manage: 'Your notebook is open.',
    fixPayment: "Your payment didn't go through.",
    fixInStore: "Your payment didn't go through.",
    trial: 'Your notebook, unlocked.',
    subscribe: 'Pick up where you left off.',
  }[action];
  const subtitle = {
    manage: periodEnd
      ? `${user.cancel_at_period_end ? 'Access ends' : 'Renews'} ${periodEnd}.`
      : 'Your subscription is active.',
    fixPayment: 'Update your card to get back in. Every page you scanned is still here.',
    fixInStore:
      'Your subscription is billed through the App Store or Google Play. Update your payment there to get back in.',
    trial: trial ? `Try everything free for ${trial}.` : 'Everything PaperBrain does, in one plan.',
    subscribe: `Every page you scanned is still here.${price ? ` ${price}, cancel anytime.` : ''}`,
  }[action];
  const onSale = action === 'trial' || action === 'subscribe';

  return (
    <AuthShell>
      {onSale && <RegistrationProgress step={2} />}
      <div className="pb-rise mt-8 flex flex-col items-center text-center">
        <NotebookCover title={notebookTitle(user.name ?? '')} />
        <h1 className="pb-heading-1 m-0 mt-8 text-[2.5rem] tracking-[-0.02em]">{title}</h1>
        <p className="m-0 mt-2 max-w-[20rem] font-body text-[0.9375rem] text-on-surface-variant">{subtitle}</p>
      </div>

      {action === 'trial' && trial && price && (
        <div className="mt-7">
          <TrialTimeline trial={trial} price={price} />
        </div>
      )}

      <div className="pb-card mt-6 px-4 py-1">
        {FEATURES.map((f, i) => (
          <div key={f} className={`flex items-center gap-3 py-3 ${i > 0 ? 'border-0 border-t border-solid border-outline-variant' : ''}`}>
            <span className="flex h-[21px] w-[21px] shrink-0 items-center justify-center rounded-full bg-surface-highlight text-accent">
              <Check size={13} strokeWidth={2.6} />
            </span>
            <span className="font-body text-[0.9375rem] text-on-surface">{f}</span>
          </div>
        ))}
      </div>

      <div className="mt-6 flex flex-col gap-3">
        {action === 'manage' && (
          <>
            <button type="button" className="pb-btn pb-btn--primary w-full py-3.5" onClick={() => navigate('/dashboard')}>
              Go to my notebook
            </button>
            <button
              type="button"
              className="pb-btn pb-btn--secondary w-full py-3.5"
              disabled={busy}
              onClick={() => void openPortal()}
            >
              Manage subscription
            </button>
          </>
        )}
        {action === 'fixPayment' && (
          <button
            type="button"
            className="pb-btn pb-btn--primary w-full py-3.5"
            disabled={busy}
            onClick={() => void openPortal()}
          >
            {busy ? 'Opening billing…' : 'Update payment method'}
          </button>
        )}
        {onSale && (
          <button
            type="button"
            className="pb-btn pb-btn--primary w-full py-3.5"
            disabled={busy || !plan}
            onClick={() => plan && void subscribe(plan.plan)}
          >
            {busy
              ? 'Opening checkout…'
              : !plan
                ? plansLoading
                  ? 'Loading…'
                  : 'Checkout is unavailable right now'
                : action === 'trial'
                  ? trialCta(plan.trial_days) ?? `Subscribe · ${price}`
                  : `Resubscribe · ${price}`}
          </button>
        )}
        <div className="flex items-center justify-center gap-2 font-body text-[0.8125rem] text-on-surface-variant">
          <Link to="/dashboard" className="text-on-surface-variant no-underline hover:text-on-surface">
            Back to app
          </Link>
          <span>·</span>
          <button
            type="button"
            className="cursor-pointer border-none bg-transparent p-0 font-body text-[0.8125rem] text-on-surface-variant hover:text-on-surface"
            onClick={() => {
              logout();
              navigate('/login');
            }}
          >
            Sign out
          </button>
        </div>
      </div>

      <p className="m-0 mt-6 text-center font-body text-xs text-on-surface-variant">
        Payments are handled by Paddle, our merchant of record. Cancel anytime from your account.
      </p>
    </AuthShell>
  );
};
